<template>
  <div class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <button class="close-button" @click="closeModal">✕</button>

      <div class="header">
        <img src="@assets/images/vehicle-info.svg" alt="Car Model Picker Header" />
        <h2>{{ $t("components.carmodelpicker.seleccionar_vehiculo") }}</h2>
      </div>

      <div class="form-container">
        <!-- Desplegable Marca -->
        <div class="field">
          <label for="marca">{{ $t("components.carmodelpicker.marca") }}</label>
          <select id="marca" v-model="selectedBrand" @change="onBrandChange" class="form-select" :disabled="isLoadingBrands">
            <option disabled value="">{{ isLoadingBrands ? $t("components.carmodelpicker.cargando_marcas") : $t("components.carmodelpicker.seleccione_marca") }}</option>
            <option v-for="brand in brands" :key="brand.id" :value="brand.id">
              {{ brand.name }}
            </option>
          </select>
        </div>

        <!-- Desplegable Modelo -->
        <div class="field">
          <label for="modelo">{{ $t("components.carmodelpicker.modelo") }}</label>
          <select id="modelo" v-model="selectedModel" :disabled="!selectedBrand || isLoadingModels" class="form-select">
            <option disabled value="">
              {{ isLoadingModels ? $t("components.carmodelpicker.cargando_modelos") : selectedBrand ? $t("components.carmodelpicker.seleccione_modelo") : $t("components.carmodelpicker.primero_seleccione_marca") }}
            </option>
            <option v-for="model in availableModels" :key="model.id" :value="model.id">
              {{ model.name }}
            </option>
          </select>
        </div>
      </div>

      <div class="actions">
        <button class="btn btn-secondary" @click="cancel">{{ $t("components.carmodelpicker.cancelar") }}</button>
        <button class="btn btn-primary" @click="accept" :disabled="!selectedBrand || !selectedModel">
          {{ $t("components.carmodelpicker.aceptar") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useApi } from "@api/integration"
// Props y emits
const emit = defineEmits<{
  close: [];
  select: [brand: string, model: string, idCocheElectrico: string];
}>();

// Estado del componente
const selectedBrand = ref<string>("");
const selectedModel = ref<string>("");

// Datos de marcas y modelos
const brands = ref<{ id: string; name: string }[]>([]);
const isLoadingBrands = ref(false);

// Inicializar API
const api = useApi();

// Función para cargar marcas desde el API
async function loadBrands() {
  isLoadingBrands.value = true;
  try {
    // Usar el patrón callStandardApi del proyecto
    const data = await api.callStandardApi(
      "getCocheElectricoMarca",  // Nombre del endpoint siguiendo el patrón camelCase
      { showLoading: false }      // No mostrar loading global, usar el local
    );
    
    console.log('✅ Datos recibidos:', data);
    
    // Transformar los datos del API al formato esperado
    brands.value = data.map((item: { Id: number; Nombre: string }) => ({
      id: item.Id.toString(),
      name: item.Nombre
    }));
    
    console.log('📝 Marcas cargadas:', brands.value);
    
  } catch (error) {
    console.error('❌ Error cargando marcas:', error);
    brands.value = [];
  } finally {
    isLoadingBrands.value = false;
  }
}

// Cargar marcas cuando se monta el componente
onMounted(() => {
  loadBrands();
});

// Modelos cargados dinámicamente
const models = ref<{ id: string; name: string }[]>([]);
const isLoadingModels = ref(false);

// Modelos disponibles según la marca seleccionada
const availableModels = computed(() => models.value);

// Funciones
async function onBrandChange() {
  selectedModel.value = ""; // Reset modelo cuando cambia la marca
  models.value = []; // Limpiar modelos anteriores
  
  // Cargar modelos de la marca seleccionada
  if (selectedBrand.value) {
    await loadModels(selectedBrand.value);
  }
}

// Función para cargar modelos según la marca seleccionada
async function loadModels(brandId: string) {
  isLoadingModels.value = true;
  try {
    console.log('🔍 Cargando modelos para marca:', brandId);
    
    // Usar el patrón callStandardApi del proyecto
    const data = await api.callStandardApi(
      "getCocheElectricoByIdMarca",  // Nombre del endpoint
      { showLoading: false },         // No mostrar loading global
      parseInt(brandId)   // Convertir a integer según swagger
    );
    
    console.log('✅ Modelos recibidos:', data);
    
    // Transformar los datos del API al formato esperado
    models.value = data.map((item: { Id: string; Modelo: string; Version?: string }) => ({
      id: item.Id,
      name: item.Version ? `${item.Modelo} ${item.Version}` : item.Modelo
    }));
    
    console.log('📝 Modelos cargados:', models.value);
    
  } catch (error) {
    console.error('❌ Error cargando modelos:', error);
    models.value = [];
  } finally {
    isLoadingModels.value = false;
  }
}

function closeModal() {
  emit("close");
}

function cancel() {
  emit("close");
}

function accept() {
  if (selectedBrand.value && selectedModel.value) {
    const brandName = brands.value.find(b => b.id === selectedBrand.value)?.name || "";
    const modelName = availableModels.value.find(m => m.id === selectedModel.value)?.name || "";
    emit("select", brandName, modelName, selectedModel.value);
    emit("close");
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/_vars.scss";
@import "@/styles/figma.scss";
@import "@/styles/_themevars.scss";

.form-container {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  padding: 0 var(--spacing-md) var(--spacing-md);
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
</style>